import { useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { twMerge } from "tailwind-merge";
import { TimezoneOption } from "@core/features/timezones";
import type { ProcessedSelectableOption } from "@core/features/scheduling";
import { RadioGroupBase } from "@core/ui";
import { ActionButtonsGroup } from "@themes/pollinator/ui/ActionButtonsGroup";
import { Checkbox } from "@themes/pollinator/ui/Checkbox";
import { Type } from "@themes/pollinator/ui/Type";

export type Props = {
  /** Whether the form is currently submitting */
  isSubmitting: boolean;
  /** Timezone for label formatting */
  timezone: TimezoneOption;
  /** Available selectable options (processed with required id/day) */
  selectableOptions: ProcessedSelectableOption[];
  /** IDs of currently selected options */
  selectedOptionIds: string[];
  /** Callback to toggle an option selection */
  onToggleOption: (optionId: string) => void;
  /** Number of options user must select */
  requiredSelections: number;
  /** Maximum number of selections allowed */
  maxSelections?: number;
  /** Current mode: 'editing' shows radio options + buttons, 'completed' shows selected + Edit */
  mode: "editing" | "completed";
  /** Callback when Edit button clicked (completed mode) */
  onEdit?: () => void;
  /** Callback when Cancel button clicked (editing mode) */
  onCancel?: () => void;
  /** Callback when Save button clicked (editing mode) */
  onSave?: () => void;
  /** Whether Save button should be disabled */
  isSaveDisabled?: boolean;
  /** Hide internal action buttons (for external action bar control) */
  hideActions?: boolean;
  /** Additional CSS classes */
  className?: string;
};

/**
 * Format an option as a label (e.g., "Fri 9am ET")
 */
function formatOptionLabel(
  option: ProcessedSelectableOption,
  timezone: TimezoneOption
): string {
  // Create UTC moments and convert to local timezone
  const localStart = moment
    .utc(option.date)
    .hour(option.startHour)
    .minute(option.startMinute)
    .tz(timezone.name);

  // Format: "h:mma" for times with minutes, "ha" for times on the hour
  const startFormat = localStart.minute() > 0 ? "h:mma" : "ha";
  const startStr = localStart.format(startFormat);

  // Format: "Fri 9am ET"
  return `${localStart.format("ddd")} ${startStr} ${timezone.abbreviation || localStart.format("z")}`;
}

/**
 * Get unique time ranges from options (for repeating mode, options with same time are grouped)
 */
function getUniqueTimeRanges(
  options: ProcessedSelectableOption[],
  timezone: TimezoneOption
): { id: string; label: string }[] {
  const seen = new Map<string, { id: string; label: string }>();

  for (const option of options) {
    // Create a key based on time range only (not date)
    const timeKey = `${option.startHour}:${option.startMinute}-${option.endHour}:${option.endMinute}`;

    if (!seen.has(timeKey)) {
      seen.set(timeKey, {
        id: option.id,
        label: formatOptionLabel(option, timezone),
      });
    }
  }

  return Array.from(seen.values());
}

/**
 * Custom radio item that shows disabled styling but still allows clicks.
 * Used to show visual feedback that selecting will replace the current selection.
 */
function SchedulingRadioItem({
  label,
  checked,
  visuallyDisabled,
  onClick,
}: {
  label: string;
  checked: boolean;
  visuallyDisabled: boolean;
  onClick: () => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [hasFocus, setHasFocus] = useState(false);

  const handleCardClick = () => {
    onClick();
    cardRef.current?.focus();
  };

  return (
    <div
      ref={cardRef}
      title={label}
      onClick={handleCardClick}
      tabIndex={0}
      role="radio"
      aria-checked={checked}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onClick();
        }
      }}
      className={twMerge(
        "flex items-center border rounded-lg p-4 shadow-md transition-shadow focus:outline-none hover:shadow-lg cursor-pointer",
        hasFocus &&
          "outline-offset-4 ring-offset-2 ring-2 ring-brand-600 ring-offset-brand-50 outline-brand-600 outline-0",
        checked && "border-brand-600 border-2",
        visuallyDisabled && !checked && "opacity-50"
      )}
    >
      <div className="flex items-center gap-2">
        <Checkbox
          name={`${label}-checkbox`}
          checked={checked}
          disabled={visuallyDisabled && !checked}
          className="h-4 w-4 border-none shadow-none mr-1 pointer-events-none"
          ariaLabel={label}
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
        />
        <div>
          <Type variant="h5" as="div" className="text-brand-700 !font-medium">
            {label}
          </Type>
        </div>
      </div>
    </div>
  );
}

/**
 * Simplified scheduling UI with RadioGroup and edit/completed modes.
 * Used for repeating-dropdown mode where users select from radio options
 * instead of a full grid.
 */
export function SchedulingRepeatingDropdown({
  isSubmitting,
  timezone,
  selectableOptions,
  selectedOptionIds,
  onToggleOption,
  requiredSelections: _requiredSelections,
  maxSelections = Infinity,
  mode,
  onEdit,
  onCancel,
  onSave,
  isSaveDisabled = false,
  hideActions = false,
  className,
}: Props) {
  // Get unique time range options for radio group
  const radioOptions = useMemo(() => {
    if (!timezone?.name) return [];
    const uniqueRanges = getUniqueTimeRanges(selectableOptions, timezone);
    return uniqueRanges.map((opt) => ({
      value: opt.id,
      label: opt.label,
    }));
  }, [selectableOptions, timezone]);

  // Get the selected option IDs for the radio group
  // (selectedOptionIds contains all option IDs for the selected time range)
  const selectedRadioValues = useMemo(() => {
    // Get unique time ranges that are selected
    const selectedTimeRanges = new Set<string>();
    for (const optionId of selectedOptionIds) {
      const option = selectableOptions.find((o) => o.id === optionId);
      if (option) {
        const timeKey = `${option.startHour}:${option.startMinute}-${option.endHour}:${option.endMinute}`;
        selectedTimeRanges.add(timeKey);
      }
    }

    // Map to radio option values
    return radioOptions
      .filter((radioOpt) => {
        const option = selectableOptions.find((o) => o.id === radioOpt.value);
        if (!option) return false;
        const timeKey = `${option.startHour}:${option.startMinute}-${option.endHour}:${option.endMinute}`;
        return selectedTimeRanges.has(timeKey);
      })
      .map((radioOpt) => radioOpt.value);
  }, [selectedOptionIds, selectableOptions, radioOptions]);

  // Handle radio selection changes
  const handleRadioChange = (newValues: (string | number)[]) => {
    // Find which value was added or removed
    const newValuesSet = new Set(newValues.map(String));
    const oldValuesSet = new Set(selectedRadioValues.map(String));

    // Find added values
    for (const value of newValuesSet) {
      if (!oldValuesSet.has(value)) {
        onToggleOption(String(value));
        return;
      }
    }

    // Find removed values
    for (const value of oldValuesSet) {
      if (!newValuesSet.has(value)) {
        onToggleOption(String(value));
        return;
      }
    }
  };

  // Completed mode: show selected option with Edit button
  if (mode === "completed") {
    const selectedOption = radioOptions.find((opt) =>
      selectedRadioValues.includes(opt.value)
    );

    return (
      <div className={twMerge("flex flex-col gap-4", className)}>
        {selectedOption && (
          <SchedulingRadioItem
            label={selectedOption.label}
            checked={true}
            visuallyDisabled={false}
            onClick={() => {}}
          />
        )}
        {!hideActions && (
          <ActionButtonsGroup
            primaryButtonText="Edit"
            onPrimaryClick={onEdit}
            className="size-max w-fit self-end"
          />
        )}
      </div>
    );
  }

  // Editing mode: show radio options with Cancel/Save buttons
  return (
    <div
      className={twMerge(
        "flex flex-col gap-4",
        isSubmitting && "opacity-20 pointer-events-none",
        className
      )}
    >
      <RadioGroupBase
        name="scheduling-options"
        options={radioOptions}
        value={selectedRadioValues}
        onChange={handleRadioChange}
        maxItems={Infinity}
        disabled={isSubmitting}
        className="flex flex-col gap-4"
        renderOption={({ option, isChecked }) => {
          // Show as visually disabled when not checked and we're at max selections
          const hasSelection = selectedRadioValues.length > 0;
          const atMaxSelections = selectedRadioValues.length >= maxSelections;
          const showAsDisabled = !isChecked && hasSelection && atMaxSelections;

          return (
            <SchedulingRadioItem
              key={option.value}
              label={option.label}
              checked={isChecked}
              visuallyDisabled={showAsDisabled}
              onClick={() => {
                // Always allow clicking - the toggle will handle replacement
                onToggleOption(String(option.value));
              }}
            />
          );
        }}
      />
      {!hideActions && (
        <ActionButtonsGroup
          secondaryButtonText="Cancel"
          onSecondaryClick={onCancel}
          primaryButtonText="Save"
          onPrimaryClick={onSave}
          primaryButtonProps={{ disabled: isSaveDisabled }}
          className="size-max w-fit self-end"
        />
      )}
    </div>
  );
}

export default SchedulingRepeatingDropdown;
